import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { SetOptional } from 'type-fest';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTextEditorProps } from '@/components/form/formAwareInputs/FormAwareTextEditor';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useFormContext } from '@/components/react-hook-form';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { GraphVizNodeConfigFormField } from '@/modules/graphViz/graphql/GraphVizNodeConfigFormFields';
import { AfterDeath } from '@/types/schema';

import {
  GenerateTileNotesCard,
  GenerateTileNotesCardProps,
} from './GenerateTileNotesCard';
import { useGenerateTileNotes } from './hooks/useGenerateTileNotes';
import { useTileNotesGenerationTarget } from './hooks/useTileNotesGenerationTarget';

type BaseTileNotesFieldProps<FormShape extends FieldValues> = SetOptional<
  FormAwareTextEditorProps<FormShape>,
  'label'
>;

interface SingleTileProps {
  mode: 'single';
  tileId: string;
}

interface GroupTileProps {
  mode: 'group';
  nodeIds: string[];
  namespace: AfterDeath;
  groupDisplayName?: string;
}

export type TileNotesFieldWithGenerateProps<FormShape extends FieldValues> =
  BaseTileNotesFieldProps<FormShape> & (SingleTileProps | GroupTileProps);

export function TileNotesFieldWithGenerate<FormShape extends FieldValues>(
  props: TileNotesFieldWithGenerateProps<FormShape>
) {
  const { mode, ...notesFieldProps } = props;

  const target = useTileNotesGenerationTarget();
  const { setValue } = useFormContext<FormShape>();
  const trackUserEvent = useTrackUserEvent();

  const [isCardOpen, setIsCardOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const onSuccess = useCallback(
    (notes: Record<string, string>) => {
      // We send a single TileNotesInput (either for a single tile or a group),
      // so we only receive one note back. Safe to take the first value.
      const generatedNote = Object.values(notes)[0] ?? '';
      const fieldName = notesFieldProps.fieldName as Parameters<
        typeof setValue
      >[0];
      setValue(fieldName, generatedNote as Parameters<typeof setValue>[1], {
        shouldDirty: true,
        shouldTouch: true,
      });
      // FormAwareTextEditor uses initialValue on mount and doesn't re-sync when
      // the form value changes externally. Incrementing resetKey forces a remount
      // so the editor picks up the new AI-generated notes.
      setResetKey((prev) => prev + 1);
      setIsCardOpen(false);
    },
    [setValue, notesFieldProps.fieldName]
  );

  const { generate, isGenerating, currentStreamingNote } = useGenerateTileNotes(
    {
      onSuccess,
    }
  );

  // Extract the mode-specific values so the memoized callbacks below depend on
  // stable primitives instead of the `props` object, which gets a fresh
  // identity on every parent render.
  const singleTileId = props.mode === 'single' ? props.tileId : undefined;
  const groupNodeIds = props.mode === 'group' ? props.nodeIds : undefined;
  const groupNamespace = props.mode === 'group' ? props.namespace : undefined;
  const groupDisplayName =
    props.mode === 'group' ? props.groupDisplayName : undefined;

  const buildInput = useCallback(() => {
    if (!target) return [];

    if (mode === 'single') {
      return singleTileId ? target.buildInputForTile(singleTileId) : [];
    }

    return target.buildInputForGroup({
      nodeIds: groupNodeIds ?? [],
      namespace: groupNamespace ?? AfterDeath.None,
      groupDisplayName,
    });
  }, [
    target,
    mode,
    singleTileId,
    groupNodeIds,
    groupNamespace,
    groupDisplayName,
  ]);

  const handleGenerate = useCallback<GenerateTileNotesCardProps['onGenerate']>(
    (prompt, promptId) => {
      if (!target) return;

      const tileCount = mode === 'single' ? 1 : (groupNodeIds?.length ?? 0);

      trackUserEvent(target.trackingEvent, {
        source: mode === 'single' ? 'TileEditModal' : 'GroupModal',
        tileCount,
        promptId,
        ...target.trackingProps,
      });

      generate(buildInput(), prompt);
    },
    [target, mode, groupNodeIds, buildInput, generate, trackUserEvent]
  );

  const handleCancel = useCallback(() => {
    setIsCardOpen(false);
  }, []);

  const handleOpenCard = useCallback(() => {
    setIsCardOpen(true);
  }, []);

  // The waterfall target always shows the generate controls; entity diagrams
  // only show them for tiles that can actually generate notes.
  const showGenerateControls = useMemo(() => {
    if (!target) return false;
    if (!target.hideWhenNoInput) return true;
    return buildInput().length > 0;
  }, [target, buildInput]);

  const isDisabled =
    notesFieldProps.disabled ||
    (mode === 'group' && (groupNodeIds?.length ?? 0) === 0);

  // Extract only FormAwareTextEditor props, filtering out mode-specific props
  const textEditorProps = (() => {
    if (mode === 'single') {
      const { tileId: _tileId, ...rest } = notesFieldProps as Omit<
        SingleTileProps,
        'mode'
      > &
        BaseTileNotesFieldProps<FormShape>;
      return rest;
    } else {
      const {
        nodeIds: _nodeIds,
        namespace: _namespace,
        groupDisplayName: _groupDisplayName,
        ...rest
      } = notesFieldProps as Omit<GroupTileProps, 'mode'> &
        BaseTileNotesFieldProps<FormShape>;
      return rest;
    }
  })();

  return (
    <Stack spacing={1}>
      <GraphVizNodeConfigFormField.Notes<FormShape>
        {...textEditorProps}
        resetKey={resetKey}
      />

      {showGenerateControls && !isCardOpen && (
        <Button
          variant="secondary"
          size="sm"
          startIcon={Stars01Icon}
          iconProps={{ size: 16 }}
          onClick={handleOpenCard}
          disabled={isDisabled}
        >
          Generate notes
        </Button>
      )}

      {showGenerateControls && isCardOpen && (
        <GenerateTileNotesCard
          onGenerate={handleGenerate}
          onCancel={handleCancel}
          isGenerating={isGenerating}
          currentStreamingNote={currentStreamingNote}
        />
      )}
    </Stack>
  );
}
